'use client';
import { useState } from 'react';
import Button from '../Button';
import ButtonContainer from '../ButtonContainer';

export default function VehicleClassifierForm() {
  const [year, setYear] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [truckAttributes, setTruckAttributes] = useState(''); // replaced "trim"
  const [result, setResult] = useState(null); // Holds the JSON result from the API
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1); // Page 1: form, Page 2: results

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Switch to page two immediately so that the spinner shows up
    setCurrentPage(2);
    setLoading(true);
    setResult(null);
    setError(null);

    const payload = { year, make, model, truckAttributes };

    try {
      const res = await fetch('/api/vehicle-classifier', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const json = await res.json();

      if (json.error) {
        setError(
          "We encountered an error computing your vehicle's classification. Please recheck your input details—adding more truck attributes (such as 'Single Cab' or 'Extended Cab') might help achieve a more accurate classification.",
        );
      } else {
        // Assuming the API returns the structured JSON result
        setResult(json);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(
        'Something went wrong. Please try again and consider adding truck attributes if applicable.',
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setYear('');
    setMake('');
    setModel('');
    setTruckAttributes('');
    setResult(null);
    setError(null);
    setLoading(false);
    setCurrentPage(1);
  };

  // Page 1: Form to enter vehicle details
  if (currentPage === 1) {
    return (
      <div className="tablet:max-w-3xl mx-auto w-full max-w-full space-y-2">
        <div className="space-y-2 rounded-xl bg-white p-8">
          <h1 className="text-secondary mb-2 text-center text-3xl font-bold">
            Vehicle Sizing Tool
          </h1>
          {/* DISCLAIMER NOTE */}

          <p>
            Our Vehicle Sizing Tool quickly classifies your vehicle using
            details like year, make, model, and truck attributes. This helps us
            determine the best service options for your ride. With fast results,
            scheduling your appointment becomes hassle-free.
          </p>
          <form onSubmit={handleSubmit} className="space-y-2">
            <div>
              <label
                htmlFor="year"
                className="mb-1 block font-semibold text-gray-600"
              >
                Year
              </label>
              <input
                type="text"
                id="year"
                name="year"
                placeholder="e.g., 2024"
                required
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 transition duration-150 focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
              />
            </div>
            <div>
              <label
                htmlFor="make"
                className="mb-1 block font-semibold text-gray-600"
              >
                Make
              </label>
              <input
                type="text"
                id="make"
                name="make"
                placeholder="e.g., Toyota"
                required
                value={make}
                onChange={(e) => setMake(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 transition duration-150 focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
              />
            </div>
            <div>
              <label
                htmlFor="model"
                className="mb-1 block font-semibold text-gray-600"
              >
                Model
              </label>
              <input
                type="text"
                id="model"
                name="model"
                placeholder="e.g., Camry"
                required
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 transition duration-150 focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
              />
            </div>
            <div>
              <label
                htmlFor="truckAttributes"
                className="mb-1 block font-semibold text-gray-600"
              >
                Vehicle Trim or Truck Attributes (Optional)
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="truckAttributes"
                  name="truckAttributes"
                  placeholder="e.g., Single Cab, Extended Cab (Optional)"
                  value={truckAttributes}
                  onChange={(e) => setTruckAttributes(e.target.value)}
                  aria-describedby="truckAttributes-hint"
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 transition duration-150 focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
                />
                <small
                  id="truckAttributes-hint"
                  className="mt-1 block text-xs text-gray-500"
                >
                  Provide additional vehicle specific or truck-specific
                  information if applicable.
                </small>
              </div>
            </div>
            <ButtonContainer className="py-1">
              <Button
                type="submit"
                className="mx-auto duration-150"
                variant="primary"
                onClick={handleSubmit}
              >
                Get My Vehicle Size
              </Button>
            </ButtonContainer>
          </form>
          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-100 p-4">
              <p className="font-semibold text-red-600">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Page 2: Display spinner while loading and then results or error
  if (currentPage === 2) {
    return (
      <div className="tablet:max-w-3xl mx-auto w-full max-w-full space-y-8 p-8">
        {loading && (
          <div className="mt-6 flex flex-col items-center">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
            <p className="mt-3 text-gray-700">We are getting your results...</p>
          </div>
        )}

        {!loading && result && (
          <div className="rounded-xl bg-white p-6">
            <h2 className="mb-4 text-2xl font-bold text-gray-800">
              Classification Results: {result.category}
            </h2>
            <p className="mb-2 text-gray-700">
              <strong>Vehicle:</strong> {result.vehicle}
            </p>
            <p className="mb-2 text-gray-700">
              <strong>Type:</strong> {result.type}
            </p>
            <p className="mb-2 text-gray-700">
              <strong>Length:</strong> {result.length} inches
            </p>
            <p className="mb-2 text-gray-700">
              <strong>Width:</strong> {result.width} inches
            </p>
            <p className="mb-2 text-gray-700">
              <strong>Passenger Capacity:</strong> {result.capacity}
            </p>
            <p className="mb-2 text-gray-700">
              <strong>Purpose:</strong> {result.purpose}
            </p>
            <div className="mt-4">
              <p className="font-semibold text-gray-800">
                Reason for Classification:
              </p>
              <p className="text-gray-700">{result.explanation}</p>
            </div>
            <p className="mt-6 text-center text-sm text-red-700">
              Disclaimer: AI determines your vehicle’s size but may not always
              be correct. Our shop makes the final call. If it seems off, please
              add details such as your vehicle’s trim or truck attributes (e.g.,
              “Single Cab” or “Extended Cab”).
            </p>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-red-200 bg-red-100 p-4">
            <p className="font-semibold text-red-600">{error}</p>
          </div>
        )}

        <ButtonContainer>
          <Button onClick={resetForm} variant="grey">
            Back to Form
          </Button>
        </ButtonContainer>
      </div>
    );
  }
}
